/**
 * Public standalone tools — shareable URLs that bring people to the site.
 *
 *   /tools                → hub listing the free tools
 *   /tools/journey-quiz   → "Where are we on the journey?" 3-question quiz
 *   /tools/guide-check    → Medicare GUIDE eligibility quick check
 *
 * All PUBLIC (no login), each with its own SeoHead, a share bar
 * (copy / text), and a CTA into /beta. Fully client-side.
 */

import React, { useState, useEffect } from "react";
import { BrandLogo } from "@/components/Brand";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { shareViaText, textShareToast } from "@/lib/sms";
import { JOURNEY_AREAS } from "@/lib/roadmapStages";
import { GuideProviderFinder } from "@/components/GuideProviderFinder";
import { track } from "@/lib/analytics";
import SeoHead from "@/components/SeoHead";
import {
    Heart, Map, Stethoscope, ArrowRight, Copy, Check, MessageSquare, Brain,
    CheckCircle2, XCircle, RefreshCw, Sparkles,
} from "lucide-react";

/* ---------------- shared shell ---------------- */

export function ToolShell({ children }) {
    return (
        <div className="min-h-screen bg-cream-light">
            <nav className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2.5" data-testid="tools-logo-home">
                    <BrandLogo className="h-6" />
                </Link>
                <Link to="/beta" className="ml-btn-primary !min-h-[40px] !py-2 text-sm" data-testid="tools-nav-cta">
                    Get the free app
                </Link>
            </nav>
            <main className="max-w-3xl mx-auto px-4 pb-16 space-y-6">{children}</main>
        </div>
    );
}

function ShareBar({ path, label }) {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}${path}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            track("tool_shared", { tool: path, method: "copy" });
        } catch { toast("Copy failed — long-press the URL to copy."); }
    };
    const text = async () => {
        const method = await shareViaText(`${label}: ${url}`);
        textShareToast(method, toast);
        track("tool_shared", { tool: path, method: "text" });
    };
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid={`share-bar-${path.replaceAll("/", "-")}`}>
            <span className="text-xs text-[#6E7A74]">Know someone who needs this?</span>
            <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-3 py-1.5 text-xs font-medium text-[#1F2624] hover:border-teal" data-testid="tool-share-copy">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} strokeWidth={2} /> Copy link</>}
            </button>
            <button type="button" onClick={text} className="inline-flex items-center gap-1.5 rounded-2xl border border-cream-dark bg-white px-3 py-1.5 text-xs font-medium text-[#1F2624] hover:border-teal" data-testid="tool-share-text">
                <MessageSquare size={12} strokeWidth={2} /> Text it
            </button>
        </div>
    );
}

function ToolCta({ src }) {
    return (
        <section className="ml-card p-5 md:p-6 bg-teal-soft !border-teal-soft" data-testid="tool-cta">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-heading font-medium text-lg text-[#1F2624]">This is a first step — the app does the rest.</p>
                    <p className="text-sm text-[#4C5551] mt-1">A personalized roadmap, an AI care companion, document prep, and a care team that stays organized. Free during beta.</p>
                </div>
                <Link to={`/beta?src=${src}`} className="ml-btn-primary shrink-0" data-testid="tool-cta-btn" onClick={() => track("tool_cta_clicked", { src })}>
                    Get your free roadmap <ArrowRight size={15} strokeWidth={2} />
                </Link>
            </div>
        </section>
    );
}

/* ---------------- /tools hub ---------------- */

const TOOLS = [
    {
        path: "/tools/memory-check",
        icon: Brain,
        name: "Memory screener — 10 warning signs",
        blurb: "Worried about a loved one's memory? 10 quick questions based on the recognized warning signs of dementia — and what to do next.",
        testid: "tool-card-memory-check",
    },
    {
        path: "/tools/journey-quiz",
        icon: Map,
        name: "Where are we on the journey?",
        blurb: "3 questions. See which part of the dementia caregiving journey you're likely in — and what usually helps there.",
        testid: "tool-card-journey-quiz",
    },
    {
        path: "/wellness-check",
        icon: Heart,
        name: "Caregiver wellness check-in",
        blurb: "One honest minute. Six sliders — burnout, sleep, stress, grief, self-care, isolation — and a reflection written for you.",
        testid: "tool-card-wellness-check",
    },
    {
        path: "/tools/guide-check",
        icon: Stethoscope,
        name: "Medicare GUIDE eligibility check",
        blurb: "5 quick questions to see if your family likely qualifies for Medicare's GUIDE dementia program — free navigator, respite hours, 24/7 line.",
        testid: "tool-card-guide-check",
    },
];

export default function ToolsHub() {
    return (
        <ToolShell>
            <SeoHead
                title="Free Dementia Caregiver Tools | Memory Lane"
                description="Free, no-signup tools for dementia caregivers: find where you are on the caregiving journey, and check Medicare GUIDE eligibility in under a minute."
                canonicalPath="/tools"
                image="https://static.prod-images.emergentagent.com/jobs/61cce65a-cd47-4af4-b633-11b331870af1/images/56bb24af745d1747038252b1f07cc5660b8e21534ea23b2c7b1f617c3faf1d8b.png"
                imageAlt="Free Caregiver Tools — no signup, 2 minutes"
            />
            <header className="pt-4" data-testid="tools-hub">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark flex items-center gap-1.5"><Sparkles size={13} strokeWidth={2.2} /> Free tools — no signup needed</p>
                <h1 className="mt-2 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]">Two minutes to a clearer next step.</h1>
                <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed">Built for dementia caregivers. Use them, share them — no account required.</p>
            </header>
            <div className="space-y-4">
                {TOOLS.map(({ path, icon: Icon, name, blurb, testid }) => (
                    <Link key={path} to={path} className="ml-card ml-card-hover p-5 md:p-6 flex items-start gap-4 block" data-testid={testid}>
                        <span className="shrink-0 w-11 h-11 rounded-2xl bg-teal-soft flex items-center justify-center">
                            <Icon size={20} strokeWidth={1.7} className="text-teal-dark" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-heading font-medium text-lg text-[#1F2624]">{name}</p>
                            <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">{blurb}</p>
                        </div>
                        <ArrowRight size={18} strokeWidth={1.8} className="shrink-0 mt-1 text-teal-dark" />
                    </Link>
                ))}
            </div>
            <ShareBar path="/tools" label="Free dementia caregiver tools" />
            <ToolCta src="tools-hub" />
        </ToolShell>
    );
}

/* ---------------- /tools/journey-quiz ---------------- */

const QUIZ = [
    {
        q: "Has a doctor given a dementia diagnosis?",
        options: [
            { label: "No — we're just noticing changes", area: 0 },
            { label: "We're in the middle of testing and appointments", area: 1 },
            { label: "Yes, recently (within ~6 months)", area: 2 },
            { label: "Yes, a while ago", area: 3 },
        ],
    },
    {
        q: "How much help does your person need day to day?",
        options: [
            { label: "Little to none — reminders here and there", area: 0 },
            { label: "Help with some daily tasks (meds, meals, bills)", area: 3 },
            { label: "Help with most things, including personal care", area: 4 },
            { label: "Full care — mostly in bed or chair", area: 5 },
        ],
    },
    {
        q: "Does any of this apply right now?",
        options: [
            { label: "None of these", area: -1 },
            { label: "A hospice team is involved (or has been suggested)", area: 5 },
            { label: "We lost our person recently", area: 6 },
            { label: "I'm mostly worried about my own exhaustion", area: -1 },
        ],
    },
];

export function JourneyQuiz() {
    const [answers, setAnswers] = useState([null, null, null]);
    const [result, setResult] = useState(null);

    const submit = () => {
        const picked = answers.filter(a => a !== null && a.area >= 0).map(a => a.area);
        const idx = picked.length ? Math.max(...picked) : 0;
        const area = JOURNEY_AREAS[Math.min(idx, JOURNEY_AREAS.length - 1)];
        setResult(area);
        track("tool_completed", { tool: "journey-quiz", result: area.key });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <ToolShell>
            <SeoHead
                title="Where Are We on the Dementia Caregiving Journey? Free 3-Question Quiz | Memory Lane"
                description="A free 3-question quiz for dementia caregivers. Find which part of the caregiving journey you're likely in — and what usually helps there. No signup."
                canonicalPath="/tools/journey-quiz"
                image="https://static.prod-images.emergentagent.com/jobs/61cce65a-cd47-4af4-b633-11b331870af1/images/dada4109aace268dd199fb822df0967945c3938aca189152a576ffd710372ff3.png"
                imageAlt="Where are we on the journey? Free 3-question quiz for dementia caregivers"
            />
            {!result ? (
                <section data-testid="journey-quiz">
                    <header className="pt-4">
                        <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">Where are we on the journey?</h1>
                        <p className="mt-2 text-[#4C5551]">Three questions. No wrong answers, no signup. This is guidance, not a clinical staging tool.</p>
                    </header>
                    {QUIZ.map((item, qi) => (
                        <div key={qi} className="ml-card p-5 mt-4" data-testid={`quiz-q-${qi}`}>
                            <p className="font-medium text-[#1F2624]">{qi + 1}. {item.q}</p>
                            <div className="mt-3 space-y-2">
                                {item.options.map((opt, oi) => (
                                    <button
                                        key={oi}
                                        type="button"
                                        data-testid={`quiz-q${qi}-opt${oi}`}
                                        onClick={() => setAnswers(prev => prev.map((a, i) => i === qi ? opt : a))}
                                        className={`w-full text-left rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${answers[qi] === opt ? "border-teal bg-teal-soft/60 text-teal-dark font-medium" : "border-cream-dark bg-white text-[#1F2624] hover:border-teal-light"}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={submit}
                        disabled={answers.some(a => a === null)}
                        className="ml-btn-primary mt-5 disabled:opacity-40"
                        data-testid="quiz-submit"
                    >
                        See where we are <ArrowRight size={15} strokeWidth={2} />
                    </button>
                </section>
            ) : (
                <section data-testid="journey-quiz-result">
                    <header className="pt-4">
                        <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: result.color }}>Your likely journey area</p>
                        <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]" data-testid="quiz-result-title">{result.title}</h1>
                        <p className="mt-2 text-[#4C5551]">{result.tagline}</p>
                    </header>
                    <div className="ml-card p-5 md:p-6 mt-4" style={{ borderLeft: `5px solid ${result.color}` }}>
                        <p className="text-sm text-[#1F2624] leading-relaxed">{result.meaning}</p>
                        <p className="mt-4 text-xs uppercase tracking-wider font-semibold" style={{ color: result.color }}>What usually helps here</p>
                        <ul className="mt-2 space-y-1.5">
                            {(result.next_steps || []).slice(0, 3).map((s, i) => (
                                <li key={i} className="flex gap-2 text-sm text-[#1F2624]"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal shrink-0" />{s}</li>
                            ))}
                        </ul>
                    </div>
                    <p className="mt-3 text-xs text-[#6E7A74] italic">Every family's path is different — you may be dealing with more than one area at once. This is personalized guidance, not a diagnosis.</p>
                    <div className="mt-5"><ToolCta src="tool-journey-quiz" /></div>
                    <button type="button" onClick={() => { setResult(null); setAnswers([null, null, null]); }} className="mt-4 text-sm text-teal-dark hover:underline inline-flex items-center gap-1.5" data-testid="quiz-retake">
                        <RefreshCw size={13} strokeWidth={2} /> Retake the quiz
                    </button>
                </section>
            )}
            <ShareBar path="/tools/journey-quiz" label="Where are we on the dementia caregiving journey? Free 3-question quiz" />
        </ToolShell>
    );
}

/* ---------------- /tools/guide-check ---------------- */

const GUIDE_QS = [
    { id: "dementia_dx", q: "Has a clinician confirmed a dementia diagnosis?", note: "Any dementia type counts — Alzheimer's, vascular, Lewy body, frontotemporal, mixed. If not yet diagnosed, a memory evaluation is the first step." },
    { id: "medicare_ab", q: "Is your person enrolled in Original Medicare, BOTH Parts A and B?", note: "GUIDE requires Original Medicare with both Part A and Part B, with Medicare as the primary payer." },
    { id: "not_ma", q: "Are they NOT enrolled in a Medicare Advantage plan — including Special Needs Plans (SNPs)?", note: "Medicare Advantage doesn't qualify for GUIDE — but you can switch to Original Medicare during open enrollment." },
    { id: "not_hospice", q: "Are they NOT enrolled in the Medicare hospice benefit?", note: "Hospice has its own comprehensive benefit; GUIDE pauses if hospice starts." },
    { id: "not_pace", q: "Are they NOT enrolled in PACE (Program of All-Inclusive Care for the Elderly)?", note: "PACE participants already receive coordinated care." },
    { id: "not_ltc_facility", q: "Do they live at home or in the community (NOT long-term in a nursing home)?", note: "GUIDE supports families caring at home or in approved residential care communities." },
];

export function GuideCheck() {
    // Phase 4: questions hydrate from the shared eligibility engine
    // (GET /documents/guide/eligibility/questions) with the local copy as an
    // instant-paint fallback, and the verdict comes from the SAME backend
    // rules (POST /documents/guide/eligibility/evaluate) that score the
    // logged-in GUIDE screener — the two flows can never drift apart.
    const [qs, setQs] = useState(GUIDE_QS);
    const [answers, setAnswers] = useState(Array(GUIDE_QS.length).fill(null));
    const [verdict, setVerdict] = useState(null);
    const done = answers.every(a => a !== null);
    const eligible = verdict ? verdict.status === "likely_eligible" : (done && answers.every(a => a === true));
    const failed = answers.map((a, i) => (a === false ? i : -1)).filter(i => i >= 0);

    useEffect(() => {
        api.get("/documents/guide/eligibility/questions", { params: { mode: "prescreen" } })
            .then(r => {
                const items = (r.data?.items || []).map(it => ({ id: it.id, q: it.label, note: it.note }));
                if (items.length) setQs(items);
            })
            .catch(() => { /* local fallback keeps the tool working */ });
    }, []);

    // Evaluate through the shared engine + remember answers so the
    // logged-in GUIDE workflow can carry them over after signup.
    useEffect(() => {
        if (!done) return;
        const byId = Object.fromEntries(qs.map((item, i) => [item.id, answers[i] ? 1 : 0]));
        try {
            localStorage.setItem("ml_guide_prescreen", JSON.stringify({ answers: byId, at: new Date().toISOString() }));
        } catch { /* private mode */ }
        api.post("/documents/guide/eligibility/evaluate", { answers: byId, mode: "prescreen" })
            .then(r => setVerdict(r.data))
            .catch(() => setVerdict(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [done, answers]);

    const setAns = (i, v) => {
        setAnswers(prev => prev.map((a, j) => j === i ? v : a));
        if (i === qs.length - 1 || answers.filter(x => x !== null).length === qs.length - 1) {
            track("tool_completed", { tool: "guide-check" });
        }
    };

    return (
        <ToolShell>
            <SeoHead
                title="Medicare GUIDE Eligibility Check — Free 1-Minute Tool | Memory Lane"
                description="Check in under a minute if your family likely qualifies for Medicare's GUIDE dementia program: a free care navigator, caregiver respite hours, and a 24/7 support line."
                canonicalPath="/tools/guide-check"
                image="https://static.prod-images.emergentagent.com/jobs/61cce65a-cd47-4af4-b633-11b331870af1/images/aae5a4e3a0d4d215a16fe34479cf75a211d760db9e3f29c138fc044af11a47e3.png"
                imageAlt="Free dementia support from Medicare — GUIDE eligibility check"
            />
            <section data-testid="guide-check">
                <header className="pt-4">
                    <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">Does your family qualify for Medicare GUIDE?</h1>
                    <p className="mt-2 text-[#4C5551]">GUIDE gives dementia families a dedicated care navigator, caregiver training, respite hours, and a 24/7 support line — at no cost. Six quick questions tell you if you likely qualify.</p>
                    <p className="mt-3 text-xs text-[#6E7A74] leading-relaxed" data-testid="guide-check-disclaimer">
                        This pre-screen does not diagnose dementia, guarantee GUIDE eligibility, or complete enrollment. Final eligibility is determined by CMS after an assessment and submission by a participating GUIDE provider.
                    </p>
                </header>
                {qs.map((item, i) => (
                    <div key={i} className="ml-card p-4 md:p-5 mt-3" data-testid={`guide-q-${i}`}>
                        <p className="font-medium text-[#1F2624] text-sm md:text-base">{i + 1}. {item.q}</p>
                        <div className="mt-2.5 flex gap-2">
                            <button type="button" data-testid={`guide-q${i}-yes`} onClick={() => setAns(i, true)}
                                className={`rounded-2xl px-4 py-1.5 text-sm font-medium border transition-colors ${answers[i] === true ? "bg-teal text-white border-teal" : "bg-white border-cream-dark text-[#1F2624] hover:border-teal"}`}>Yes</button>
                            <button type="button" data-testid={`guide-q${i}-no`} onClick={() => setAns(i, false)}
                                className={`rounded-2xl px-4 py-1.5 text-sm font-medium border transition-colors ${answers[i] === false ? "bg-[#4C5551] text-white border-[#4C5551]" : "bg-white border-cream-dark text-[#1F2624] hover:border-[#4C5551]"}`}>No / Not sure</button>
                        </div>
                        {answers[i] === false && <p className="mt-2 text-xs text-[#6E7A74]">{item.note}</p>}
                    </div>
                ))}
                {done && (
                    <div className={`ml-card p-5 md:p-6 mt-4 ${eligible ? "bg-teal-soft !border-teal-soft" : ""}`} data-testid="guide-check-result">
                        {eligible ? (
                            <div className="flex items-start gap-3">
                                <CheckCircle2 size={22} strokeWidth={1.8} className="text-teal-dark shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-heading font-medium text-lg text-[#1F2624]">Your family likely qualifies for GUIDE. 🎉</p>
                                    <p className="text-sm text-[#4C5551] mt-1">Next: find a participating GUIDE provider and bring a one-page summary of your situation. Memory Lane preps the whole packet — eligibility answers, screeners, and a shareable link your provider can read in two minutes. The provider completes the official assessment and CMS makes the final eligibility decision.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-3">
                                <XCircle size={22} strokeWidth={1.8} className="text-[#6E7A74] shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-heading font-medium text-lg text-[#1F2624]">GUIDE may not fit yet — but you still have options.</p>
                                    <p className="text-sm text-[#4C5551] mt-1">See the notes under your &ldquo;No&rdquo; answers ({failed.map(i => `Q${i + 1}`).join(", ")}). Many families become eligible after an enrollment change or a formal diagnosis — and other programs can help meanwhile.</p>
                                    {verdict?.blockers?.length > 0 && (
                                        <ul className="mt-2 text-xs text-[#6E7A74] list-disc pl-4 space-y-0.5" data-testid="guide-check-blockers">
                                            {verdict.blockers.map((b, i) => <li key={i}>{b}</li>)}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {done && eligible && <GuideProviderFinder basePath="/resources" source="guide-check-provider-finder" />}
                {done && <div className="mt-4"><ToolCta src="tool-guide-check" /></div>}
            </section>
            <ShareBar path="/tools/guide-check" label="Free 1-minute Medicare GUIDE eligibility check for dementia families" />
        </ToolShell>
    );
}

/* ------------------------------------------------------------------ */
/* Memory screener — 10 warning signs                                  */
/* ------------------------------------------------------------------ */
const MEMORY_QS = [
    { q: "Memory loss that disrupts daily life?", note: "Forgetting recently learned information, asking the same question over and over, relying heavily on notes or family for things they used to handle." },
    { q: "New trouble planning or solving problems?", note: "Struggling to follow a familiar recipe, keep track of monthly bills, or concentrate on tasks that used to be easy." },
    { q: "Difficulty completing familiar tasks?", note: "Trouble driving to a well-known place, organizing a grocery list, or remembering the rules of a favorite game." },
    { q: "Confusion with time or place?", note: "Losing track of dates or seasons, or sometimes forgetting where they are or how they got there." },
    { q: "New trouble with vision or judging distance?", note: "Difficulty reading, judging distance, or telling colors apart — enough to affect driving or getting around." },
    { q: "New problems with words?", note: "Stopping mid-sentence with no idea how to continue, repeating themselves, or calling things by the wrong name (\"hand-clock\" for watch)." },
    { q: "Misplacing things and losing the ability to retrace steps?", note: "Putting things in unusual places (keys in the freezer) and being unable to go back over their steps — sometimes accusing others of stealing." },
    { q: "Decreased or poor judgment?", note: "Falling for scams or giveaways, poor money decisions, or paying less attention to grooming and cleanliness." },
    { q: "Withdrawal from work or social activities?", note: "Dropping hobbies, social events, or projects — often because keeping up has become hard or embarrassing." },
    { q: "Changes in mood or personality?", note: "Becoming confused, suspicious, depressed, fearful, or anxious — especially when out of their comfort zone." },
];

export function MemoryCheck() {
    const [answers, setAnswers] = useState({});
    const done = Object.keys(answers).length === MEMORY_QS.length;
    const yesCount = Object.values(answers).filter(Boolean).length;
    useEffect(() => {
        if (done) track("tool_completed", { tool: "memory-check", yes_count: yesCount });
    }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

    const band = yesCount <= 1
        ? { tone: "#3A6C6A", title: "Mostly reassuring — keep gently watching.",
            body: "Occasional slips happen to everyone, especially with stress, poor sleep, or grief. Jot down specific examples with dates. If new signs appear, or the ones you noticed happen more often, bring the list to their doctor." }
        : yesCount <= 3
            ? { tone: "#B7791F", title: "Worth a conversation with their doctor.",
                body: "A few warning signs don't mean dementia — thyroid problems, vitamin B12 deficiency, medication side effects, depression, and sleep apnea can all look similar and are treatable. Bring your specific examples to a doctor visit and ask for a cognitive check." }
            : { tone: "#C05353", title: "Please schedule a memory evaluation soon.",
                body: "Several warning signs together deserve a proper evaluation — not to scare you, but because some causes are treatable, and if it is dementia, an early diagnosis opens doors: treatment options, planning time, and support programs like Medicare GUIDE." };

    return (
        <ToolShell>
            <SeoHead
                title="Free Memory Screener — 10 Warning Signs of Dementia | Memory Lane"
                description="Worried about a loved one's memory? A free 2-minute screener based on the 10 recognized warning signs of Alzheimer's and dementia — and what to do next."
                canonicalPath="/tools/memory-check"
                image="https://static.prod-images.emergentagent.com/jobs/61cce65a-cd47-4af4-b633-11b331870af1/images/fbffa6c2624068f86a40316abbd4eaa36fa59bc5e745358d603ff8d52f9244af.png"
                imageAlt="Is it more than forgetfulness? Free 2-minute memory screener"
            />
            <section data-testid="tool-memory-check">
                <header className="pt-4">
                    <Link to="/tools" className="text-xs text-teal-dark font-medium">← All free tools</Link>
                    <h1 className="mt-2 text-4xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F]">Worried about their memory?</h1>
                    <p className="mt-2 text-[#4C5551]">10 quick questions based on the recognized warning signs of dementia. Answer for the person you&apos;re worried about — go with your gut.</p>
                    <p className="mt-3 text-xs text-[#6E7A74] leading-relaxed" data-testid="memory-check-disclaimer">
                        This screener does not diagnose dementia or any medical condition. Only a clinician can evaluate memory changes — many causes are treatable and have nothing to do with dementia.
                    </p>
                </header>
                <div className="mt-6 space-y-3">
                    {MEMORY_QS.map((item, i) => (
                        <div key={i} className="ml-card p-4 md:p-5" data-testid={`memory-q-${i}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <p className="font-medium text-[#1F2624]">{i + 1}. {item.q}</p>
                                    <p className="text-xs text-[#6E7A74] mt-1 leading-relaxed">{item.note}</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    {[["Yes", true], ["No", false]].map(([label, val]) => (
                                        <button
                                            key={label}
                                            type="button"
                                            onClick={() => setAnswers(a => ({ ...a, [i]: val }))}
                                            data-testid={`memory-q${i}-${label.toLowerCase()}`}
                                            className={`px-4 py-1.5 rounded-2xl text-sm font-medium border transition-colors ${answers[i] === val
                                                ? (val ? "bg-coral text-white border-coral" : "bg-teal text-white border-teal")
                                                : "bg-white text-[#4C5551] border-cream-dark hover:border-teal"}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {done && (
                    <div className="ml-card p-5 md:p-6 mt-5 border-2" style={{ borderColor: band.tone }} data-testid="memory-check-result">
                        <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: band.tone }}>{yesCount} of 10 warning signs</p>
                        <p className="font-heading font-medium text-lg text-[#1F2624] mt-1">{band.title}</p>
                        <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">{band.body}</p>
                        <p className="text-sm text-[#4C5551] mt-3 leading-relaxed">
                            Helpful next reads: <Link to="/learn/what-is-dementia" className="text-teal-dark underline">What is dementia?</Link> · <Link to="/learn/early-warning-signs" className="text-teal-dark underline">Early warning signs</Link> · <Link to="/tools/guide-check" className="text-teal-dark underline">Medicare GUIDE eligibility check</Link>
                        </p>
                    </div>
                )}
                {done && <div className="mt-4"><ToolCta src="tool-memory-check" /></div>}
            </section>
            <ShareBar path="/tools/memory-check" label="Free 2-minute memory screener — the 10 warning signs of dementia" />
        </ToolShell>
    );
}
